import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  CircularProgress, 
  Box, 
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { formatDate } from '../../utils/formatDate';


const BannersList = () => {
  const { data: config } = useSelector((state) => state.config);
  const [banners, setBanners] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const navigate = useNavigate();

  // Fetch banners on component mount and when config changes
  useEffect(() => {
    if (config?.API_BASE_URL) {
      fetchBanners();
    }
  }, [config?.API_BASE_URL]);

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${config.API_BASE_URL}/api/banners`);
      // Ensure banners have an order field, defaulting to their index if not set
      const bannersWithOrder = response.data.map((banner, index) => ({
        ...banner,
        order: banner.order !== undefined ? banner.order : index
      }));
      // Sort banners by order
      bannersWithOrder.sort((a, b) => a.order - b.order);
      setBanners(bannersWithOrder);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  }, [config.API_BASE_URL]);

  const filteredBanners = banners
    .filter(
      (banner) =>
        banner.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (banner.description && banner.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleAddDialogOpen = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddDialogClose = useCallback(() => {
    setIsAddDialogOpen(false);
    fetchBanners();
  }, [fetchBanners]);

  const handleEditDialogOpen = (banner) => {
    setSelectedBanner(banner);
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = useCallback(() => {
    setIsEditDialogOpen(false);
    setSelectedBanner(null);
    fetchBanners();
  }, [fetchBanners]);

  const handleDelete = useCallback(async (bannerId) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await apiClient.delete(`${config.API_BASE_URL}/api/banners/${bannerId}`);
        fetchBanners();
      } catch (error) {
        console.error('Error deleting banner:', error);
      }
    }
  }, [config?.API_BASE_URL, fetchBanners]);

  const toggleStatus = useCallback(async (banner) => {
    try {
      await apiClient.put(`${config.API_BASE_URL}/api/banners/${banner._id}/toggle-status`);
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner status:', error);
    }
  }, [config?.API_BASE_URL, fetchBanners]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Add Banner Dialog Component
  const BannerDialog = ({ open, onClose, onBannerAdded, banner = null }) => {
    const [title, setTitle] = useState(banner?.title || '');
    const [image, setImage] = useState(banner?.image || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        const formData = new FormData();
        formData.append('title', title);
        if (image && image instanceof File) {
          formData.append('image', image);
        }

        if (banner) {
          await apiClient.put(`${config.API_BASE_URL}/api/banners/${banner._id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          await apiClient.post(`${config.API_BASE_URL}/api/banners`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }

        onBannerAdded();
        onClose();
      } catch (err) {
        setError(err.response?.data?.message || 'An error occurred');
        console.error('Error saving banner:', err);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{banner ? 'Edit Banner' : 'Add New Banner'}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImage(e.target.files[0])}
              />
              {error && (
                <Box color="error.main" sx={{ mt: 1 }}>
                  {error}
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            label="Search Banners"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddDialogOpen}
              sx={{ ml: 2 }}
            >
              Add Banner
            </Button>
          </Box>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBanners
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((banner) => (
                <TableRow key={banner._id}>
                        <TableCell>
                          <Box
                            onClick={() => {
                              setSelectedImage(banner.image.startsWith('http') ? banner.image : `${config.API_BASE_URL}${banner.image}`);
                              setPreviewOpen(true);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 },
                              width: 100,
                              height: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden'
                            }}
                          >
                            <img
                              src={banner.image.startsWith('http') ? banner.image : `${config.API_BASE_URL}${banner.image}`}
                              alt={banner.title}
                              style={{ 
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/100x60?text=Image+Not+Found';
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{banner.title}</TableCell>
                        <TableCell>
                          <Chip
                            label={banner.isActive ? 'Active' : 'Inactive'}
                            color={banner.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(banner.createdAt)}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => navigate(`/banners/${banner._id}`)}
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleEditDialogOpen(banner)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => toggleStatus(banner)}
                            color={banner.isActive ? 'warning' : 'success'}
                          >
                            {banner.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(banner._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredBanners.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <BannerDialog
        open={isAddDialogOpen}
        onClose={handleAddDialogClose}
        onBannerAdded={fetchBanners}
      />

      {selectedBanner && (
        <BannerDialog
          open={isEditDialogOpen}
          onClose={handleEditDialogClose}
          onBannerUpdated={fetchBanners}
          initialBanner={selectedBanner}
        />
      )}
    </Box>
  );

  return (
    <>
      
      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Image Preview
          <IconButton
            aria-label="close"
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <img
            src={selectedImage}
            alt="Full size preview"
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BannersList;
